import { ResponsiveBar, type BarCustomLayerProps } from "@nivo/bar";
import { line as d3Line, curveMonotoneX } from "d3-shape";
import type { AttributeDistribution } from "@/repo/attribute";

interface AttributeDistributionChartProps {
    data: AttributeDistribution;
}

type Bin = AttributeDistribution["distribution"][number] & {
    binLabel: string;
    index: number;
};

const UPRN_LINE_COLOR = "#5c9dea";

/** Draws the UPRN (address) count as a line over the cell-count bars,
 * normalised to its own max — the two series differ by orders of magnitude
 * (tens of millions of addresses vs thousands of cells), so sharing the bars'
 * y-scale would flatten one of them. The line's shape (not its absolute
 * height) is what shows whether addresses skew toward higher- or
 * lower-scoring cells than a plain cell count would suggest; hover a point
 * for the exact UPRN count. */
function UprnLineLayer(props: BarCustomLayerProps<Bin>) {
    const points = props.bars.map((bar) => ({
        x: bar.x + bar.width / 2,
        uprnCount: Number(bar.data.data.uprn_count ?? 0),
    }));
    const maxUprn = Math.max(...points.map((p) => p.uprnCount), 1);
    const yFor = (uprnCount: number) =>
        props.innerHeight - (uprnCount / maxUprn) * props.innerHeight;

    const path = d3Line<{ x: number; uprnCount: number }>()
        .x((p) => p.x)
        .y((p) => yFor(p.uprnCount))
        .curve(curveMonotoneX)(points);

    if (!path) return null;

    return (
        <g>
            <path
                d={path}
                fill="none"
                stroke={UPRN_LINE_COLOR}
                strokeWidth={2}
            />
        </g>
    );
}

function AttributeDistributionChart(props: AttributeDistributionChartProps) {
    if (!props.data) return null;
    // uprn_count is only populated for the cell/token-level distribution
    // (places have no UPRN concept) — skip the line when it's not meaningful
    // rather than draw a flat one along the bottom.
    const hasUprnData = props.data.uprn_count > 0;

    return (
        <>
            <dl>
                <dt>Min</dt>
                <dd>{props.data.min.toFixed(4)}</dd>
                <dt>Max</dt>
                <dd>{props.data.max.toFixed(4)}</dd>
                <dt>Count</dt>
                <dd>{props.data.count}</dd>
                {hasUprnData && (
                    <>
                        <dt>UPRNs</dt>
                        <dd>{props.data.uprn_count}</dd>
                    </>
                )}
            </dl>
            <div style={{ height: "260px" }}>
                <ResponsiveBar
                    data={props.data.distribution.map((d, index) => ({
                        ...d,
                        binLabel: `${d.min.toFixed(2)} - ${d.max.toFixed(2)}`,
                        index,
                    }))}
                    keys={["count"]}
                    indexBy="binLabel"
                    margin={{ top: 20, right: 20, bottom: 80, left: 50 }}
                    padding={0.1}
                    valueScale={{ type: "linear", round: true }}
                    colors={"#ea7d5455"}
                    borderRadius={0}
                    axisBottom={{
                        tickPadding: 5,
                        tickSize: 0,
                        tickValues: [],
                        format: () => null,
                    }}
                    axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                    }}
                    enableLabel={false}
                    theme={{
                        grid: {
                            line: {
                                stroke: "#ffffff11",
                                strokeWidth: 1,
                            },
                        },
                        axis: {
                            ticks: { text: { fill: "#ffffffaa" } },
                            legend: { text: { fill: "#8888ff" } },
                        },
                    }}
                    borderColor="#ea7d54"
                    borderWidth={1}
                    layers={
                        hasUprnData
                            ? [
                                  "grid",
                                  "axes",
                                  "bars",
                                  "markers",
                                  UprnLineLayer,
                                  "legends",
                                  "annotations",
                              ]
                            : [
                                  "grid",
                                  "axes",
                                  "bars",
                                  "markers",
                                  "legends",
                                  "annotations",
                              ]
                    }
                    tooltip={({ data, value }) => (
                        <div
                            style={{
                                padding: 12,
                                color: "#fff",
                                background: "#222",
                                borderRadius: 4,
                            }}
                        >
                            {typeof data?.min === "number" &&
                                typeof data?.max === "number" && (
                                    <>
                                        {(data.min / 100).toFixed(2)}
                                        <br />
                                    </>
                                )}
                            <strong>Count:</strong> {value}
                            {typeof data?.uprn_count === "number" && (
                                <>
                                    <br />
                                    <strong>UPRNs:</strong>{" "}
                                    {data.uprn_count.toLocaleString()}
                                </>
                            )}
                        </div>
                    )}
                    role="application"
                    ariaLabel="Attribute Distribution Histogram"
                />
            </div>
        </>
    );
}

export default AttributeDistributionChart;
